/**
 * import-status-badge.tsx — where a bulk import job stands, as its history and
 * its detail say it: one table of each status's words, tone and mark.
 */

import { useTranslation } from 'react-i18next';
import { Pill, type PillTone } from '../../../components/pill';

/**
 * Each job status the badge names (the backend's ImportJob.status): its words,
 * a key written whole, its tone and its mark, from one table. The status is a
 * string on the wire, so one not here shows as the server sends it, in
 * neutral. Failed and aborted are alike; a partial import is neither a
 * failure nor a success — it applied some operations and not others, and
 * either tone would hide one half.
 */
const IMPORT_STATUS_LOOK: Partial<Record<string, { key: string; tone: PillTone; mark: string }>> = {
  pending: { key: 'admin.bulkImportHistory.statusBadges.pending', tone: 'neutral', mark: '·' },
  running: { key: 'admin.bulkImportHistory.statusBadges.running', tone: 'info', mark: '⏳' },
  completed: { key: 'admin.bulkImportHistory.statusBadges.completed', tone: 'success', mark: '✅' },
  partial: { key: 'admin.bulkImportHistory.statusBadges.partial', tone: 'warning', mark: '⚠' },
  failed: { key: 'admin.bulkImportHistory.statusBadges.failed', tone: 'danger', mark: '❌' },
  aborted: { key: 'admin.bulkImportHistory.statusBadges.aborted', tone: 'danger', mark: '🛑' },
};

export function ImportStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const look = IMPORT_STATUS_LOOK[status];
  return (
    <Pill tone={look?.tone ?? 'neutral'}>
      {look?.mark ?? '·'} {look ? t(look.key) : status}
    </Pill>
  );
}
